import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Button, ErrorText, Input, Loading } from '../components/ui';
import { errorMessage } from '../lib/api';
import { timeAgo, timecode } from '../lib/format';
import { viewerName } from '../lib/storage';
import type { Comment } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchComments, postComment } from '../queries/share';

/**
 * Timecoded comments.
 *
 * The timecode is the point: "2:14 - can we change this?" is a review note,
 * where "can we change this?" is a riddle. Clicking one seeks the player, so
 * feedback and the frame it is about stay attached.
 */

function CommentBody({
  comment,
  onSeek,
}: {
  comment: Comment;
  onSeek: (seconds: number) => void;
}) {
  const author = comment.authorUser?.name ?? comment.authorName ?? 'Guest';

  return (
    <li className="border-b border-hairline py-3 last:border-b-0">
      <div className="flex items-baseline gap-2">
        <span className="text-body font-medium text-chalk">{author}</span>
        <span className="text-caption text-chalk-faint">{timeAgo(comment.createdAt)}</span>
      </div>
      <div className="mt-1 flex items-start gap-2">
        {comment.timecodeSec !== null && comment.timecodeSec !== undefined ? (
          <button
            type="button"
            onClick={() => onSeek(comment.timecodeSec ?? 0)}
            data-timecode
            className="mt-0.5 shrink-0 rounded-xs border border-hairline bg-slate-900 px-1.5 py-0.5 font-mono text-micro text-signal transition-colors duration-120 hover:border-signal/50 hover:bg-signal/10"
            title="Jump to this moment"
          >
            {timecode(comment.timecodeSec)}
          </button>
        ) : null}
        <p className="whitespace-pre-wrap text-body text-chalk-dim">{comment.bodyText}</p>
      </div>
    </li>
  );
}

/** Keeps the "attach the current time" hint honest as the media plays. */
function useCurrentTime(mediaRef: RefObject<HTMLMediaElement | null>, attached: boolean): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const update = () => setValue(media.currentTime);
    media.addEventListener('timeupdate', update);
    media.addEventListener('seeked', update);
    return () => {
      media.removeEventListener('timeupdate', update);
      media.removeEventListener('seeked', update);
    };
    // `attached` flips when the player appears: a recording that was still
    // processing when this mounted needs the listeners hooked up afterwards.
  }, [attached, mediaRef]);

  return value;
}

export function Comments({
  shareId,
  mediaRef,
  playerReady = false,
  authorName,
  focusToken = 0,
  guard,
}: {
  shareId: string;
  /** Who recorded it, for "Respond to Garrett". */
  authorName?: string | null;
  /** Bump to put focus in the composer (the Comment button, the C key). */
  focusToken?: number;
  /** Runs the action, or asks the visitor to sign in first. */
  guard?: (purpose: 'react' | 'comment', then: () => void) => void;
  /**
   * The video or audio element being played. Comments are kind-agnostic: a
   * timecode on a voice note means exactly what it means on a screen share.
   */
  mediaRef: RefObject<HTMLMediaElement | null>;
  /** True once a playable media element exists. */
  playerReady?: boolean;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [name, setName] = useState(() => viewerName.get());
  const [attachTimecode, setAttachTimecode] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  // One line until it is touched, then the whole form.
  const [open, setOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (focusToken === 0) return;
    setOpen(true);
    requestAnimationFrame(() => composerRef.current?.focus());
  }, [focusToken]);
  const currentTime = useCurrentTime(mediaRef, playerReady);

  const query = useQuery({
    queryKey: qk.comments(shareId),
    queryFn: () => fetchComments(shareId),
    staleTime: 15_000,
  });

  const mutation = useMutation({
    mutationFn: (input: { bodyText: string; authorName?: string; timecodeSec?: number }) =>
      postComment(shareId, input),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: qk.comments(shareId) });
    },
  });

  const seek = (seconds: number) => {
    const media = mediaRef.current;
    if (!media) return;
    media.currentTime = seconds;
    void media.play().catch(() => {
      // Autoplay restrictions; the seek still happened.
    });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const text = body.trim();
    if (!text) {
      setFormError('Write something first.');
      return;
    }
    if (!user && !name.trim()) {
      setFormError('Add your name so they know who this is from.');
      return;
    }

    if (!user) viewerName.set(name.trim());

    const currentTime = mediaRef.current?.currentTime ?? 0;
    mutation.mutate({
      bodyText: text,
      ...(user ? {} : { authorName: name.trim() }),
      ...(attachTimecode && currentTime > 0 ? { timecodeSec: Math.floor(currentTime) } : {}),
    });
  };

  const comments = query.data ?? [];

  const who = authorName?.trim() ? authorName.trim().split(' ')[0] : null;
  const initial = (user?.displayName || name || '?').trim().charAt(0).toUpperCase();

  return (
    <section className="space-y-4">
      <form onSubmit={submit} className="rounded-md border border-hairline bg-slate-900/60 p-3">
        <div className="flex items-start gap-2.5">
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-700 text-caption text-chalk"
          >
            {initial}
          </span>
          <textarea
            ref={composerRef}
            rows={open ? 3 : 1}
            value={body}
            maxLength={4000}
            aria-label="Comment"
            placeholder={who ? `Respond to ${who}\u2026` : 'Leave a comment\u2026'}
            onFocus={() => {
              if (!user && guard) {
                composerRef.current?.blur();
                guard('comment', () => setOpen(true));
                return;
              }
              setOpen(true);
            }}
            onChange={(event) => setBody(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') submit(event);
            }}
            className="min-w-0 flex-1 resize-none bg-transparent py-1 text-body text-chalk placeholder:text-chalk-faint focus:outline-none"
          />
        </div>
        {open ? (
          <div className="mt-2 space-y-2 pl-9">
            {!user ? (
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your name"
                maxLength={80}
                aria-label="Your name"
              />
            ) : null}
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-caption text-chalk-dim">
                <input
                  type="checkbox"
                  checked={attachTimecode}
                  onChange={(event) => setAttachTimecode(event.target.checked)}
                  className="h-3.5 w-3.5 rounded-xs border-hairline bg-slate-900 accent-signal"
                />
                <span>
                  At{' '}
                  <span data-timecode className="font-mono text-chalk-faint">
                    {timecode(currentTime)}
                  </span>
                </span>
              </label>
              <Button type="submit" variant="primary" size="sm" busy={mutation.isPending}>
                Comment
              </Button>
            </div>
            <ErrorText>{formError ?? (mutation.isError ? errorMessage(mutation.error) : null)}</ErrorText>
          </div>
        ) : null}
      </form>

      {query.isLoading ? <Loading label="Loading comments" /> : null}

      {query.isError ? (
        <p className="text-caption text-chalk-faint">
          Comments could not be loaded right now. {errorMessage(query.error)}
        </p>
      ) : null}

      {!query.isLoading && !query.isError && comments.length === 0 ? (
        <div className="py-10 text-center">
          <p className="text-h4 text-chalk">Be the first to comment</p>
          <p className="mx-auto mt-1.5 max-w-xs text-caption text-chalk-dim">
            Press <kbd className="rounded-xs border border-hairline px-1 font-mono text-micro">C</kbd>{' '}
            to reply{who ? ` to ${who}` : ''}, or write at the top of this panel. With the time attached,
            they land on the exact moment you meant.
          </p>
        </div>
      ) : null}

      {comments.length > 0 ? (
        <ul>
          {comments.map((comment) => (
            <CommentBody key={comment.id} comment={comment} onSeek={seek} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
